import React, { useState, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { userDelete } from '../../services/MainApiDetails';
import {InfoOutlined} from "@mui/icons-material"
import { ERROR, ERROR_BG, ERROR_BORDER, ERROR_BORDER_LIGHT, ERROR_DARK } from '../../utilities/colors';

const translations = {
  en: {
    title: "Delete Account",
    question: "Are you sure you want to delete your account?",
    warning: "This action cannot be undone",
    prediction: "Your profile and prediction history will no longer be accessible after deletion.",
    data: "If you register again using the same email or phone number, your previous account data will not be restored.",
    subtleNote: "Certain system records may continue to exist as part of contest results or previously completed activities, such as verifying past winners.",
    cancel: "Cancel",
    success: "Account deleted successfully",
    error: "Failed to delete account. Please try again.",
    errorOccurred: "An error occurred while removing your account. Please try again."
  },
  ml: {
    title: "അക്കൗണ്ട് ഡിലീറ്റ് ചെയുക ",
    question: "നിങ്ങളുടെ അക്കൗണ്ട് ഡിലീറ്റ് ചെയ്യണമെന്ന്  ഉറപ്പാണോ?",
    warning: "ഡിലീറ്റ് ചെയ്ത അക്കൗണ്ട് വീണ്ടും തിരിച്ചു എടുക്കാൻ കഴിയില്ല ",
    prediction: "നിങ്ങൾ ചെയ്ത എല്ലാ പ്രവചനങ്ങളും അക്കൗണ്ടിലെ വിവരങ്ങളും ഡിലീറ്റ് ചെയ്യപ്പെടും.",
    data: "പിന്നീട് ഇതേ ഇമെയിൽ അല്ലെങ്കിൽ ഫോൺ നമ്പർ ഉപയോഗിച്ച് വീണ്ടും രജിസ്റ്റർ ചെയ്താലും, പഴയ ഡാറ്റ ഒന്നും തിരിച്ചുകിട്ടുകയില്ല.",
    subtleNote:"മത്സരഫലങ്ങളുമായി ബന്ധപ്പെട്ടതോ അല്ലെങ്കിൽ മുൻ വിജയികളെ സ്ഥിരീകരിക്കാൻ ആവശ്യമായതോ ആയ ചില ഡാറ്റകൾ തുടർന്നും നിലനിൽക്കാൻ സാധ്യതയുണ്ട്.",
    cancel: "റദ്ദാക്കുക",
    success: "അക്കൗണ്ട് വിജയകരമായി ഡിലീറ്റ് ചെയ്യപ്പട്ടു ",
    error: "അക്കൗണ്ട് ഡിലീഷൻ പരാജയപ്പെട്ടു. വീണ്ടും ശ്രമിക്കുക.",
    errorOccurred: "സാങ്ങേതിക തകരാറു മൂലം അക്കൗണ്ട് ഡിലീഷൻ തടസ്സപെട്ടു. വീണ്ടും ശ്രമിക്കുക."
  }
};

export default function DeleteAccountModal({ showDeleteAccount, setShowDeleteAccount }) {
  const [loading, setLoading] = useState(false);
  const language = localStorage.getItem("language") || "en";
  const t = useMemo(() => translations[language] || translations.en, [language]);
  console.log("Selected language:", language);

  const handleClose = () => {
    if (!loading) {
      setShowDeleteAccount(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      const res = await userDelete();
      if (res.data.status === 1) {
        sessionStorage.clear();
        localStorage.clear();
        handleClose();
        window.location.href = "/";
        alert(t.success);
      } else {
        alert(t.error);
        setLoading(false);
      }
    } catch (e) {
      console.error("Error deleting account:", e);
      alert(t.errorOccurred);
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={showDeleteAccount} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <WarningAmberIcon
            sx={{
              color: ERROR,
              fontSize: 32
            }}
          />
          <Typography variant="h6" component="span" fontWeight="600" color="text.primary" fontFamily="Manrope, sans-serif">
            {t.title}
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        <Typography variant="body1" color="text.primary" fontFamily="Manrope, sans-serif" sx={{ fontSize: '0.9rem', mb: 2 }}>
          {t.question}
        </Typography>

        <Box 
          sx={{ 
            p: 2.5, 
            bgcolor: ERROR_BG,
            borderRadius: 2,
            border: `2px solid ${ERROR_BORDER}`,
            boxShadow: '0 2px 8px rgba(211,47,47,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <WarningAmberIcon sx={{ color: ERROR, fontSize: 22 }} />
            <Typography variant="subtitle2" fontWeight="700" color={ERROR} fontFamily="Manrope, sans-serif">
              {t.warning}
            </Typography>
          </Box>
          
          <Divider sx={{ mb: 2, borderColor: ERROR_BORDER_LIGHT }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <DeleteForeverIcon sx={{ fontSize: 22, color: ERROR, mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 ,fontSize:12}} fontFamily="Manrope, sans-serif">
                {t.prediction}
              </Typography>
            </Box>

            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <InfoOutlined sx={{ fontSize: 22, color: ERROR, mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6,fontSize:12 }} fontFamily="Manrope, sans-serif">
                {t.subtleNote}
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
        <Button 
          onClick={handleClose} 
          variant="outlined"
          disabled={loading}
          sx={{ 
            // minWidth: 110,
            textTransform: 'none',
            fontWeight: 600,
            fontFamily:"Manrope, sans-serif",
            fontSize: '0.95rem'
          }}
        >
          {"Cancel"}
        </Button>
        <Button 
          onClick={handleDeleteAccount} 
          variant="contained" 
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          sx={{ 
            flex:1,
            textTransform: 'none',
            fontWeight: 600,
            fontFamily:"Manrope, sans-serif",
            fontSize: '0.9rem',
            bgcolor: ERROR,
            '&:hover': {
              bgcolor: ERROR_DARK
            }
          }}
        >
          {loading ? "Deleting" : "Delete Account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}